import { supabase } from '../lib/supabase';

export type ActivityType =
  | 'payment_added'
  | 'payment_deleted'
  | 'resident_added'
  | 'resident_edited'
  | 'resident_moved'
  | 'resident_deleted'
  | 'payment_verified'
  | 'room_updated';

interface LogActivityParams {
  activityType: ActivityType;
  description: string;
  roomNumber?: string;
  residentName?: string;
  metadata?: Record<string, any>;
}

// Get user IDs for notification recipients (irfan, yudha, oeoel)
const NOTIFICATION_RECIPIENTS = ['irfan', 'yudha', 'oeoel'];

/**
 * Get current logged-in username from localStorage
 */
function getCurrentUsername(): string {
  try {
    const username = localStorage.getItem('username');
    return username || 'system';
  } catch (error) {
    console.error('Error getting current username:', error);
    return 'system';
  }
}

/**
 * Log activity and create notifications for admin users
 */
export async function logActivity({
  activityType,
  description,
  roomNumber,
  residentName,
  metadata
}: LogActivityParams): Promise<void> {
  try {
    // Get current user who performed the action
    const actorUsername = getCurrentUsername();
    
    // Create notification for each recipient using username directly
    const notifications = NOTIFICATION_RECIPIENTS.map(username => ({
      username: username,
      actor_username: actorUsername, // Who performed the action
      activity_type: activityType,
      description,
      room_number: roomNumber,
      resident_name: residentName,
      metadata,
      is_read: false
    }));

    const { error } = await supabase
      .from('notifications')
      .insert(notifications);

    if (error) throw error;

    console.log('✅ Activity logged:', activityType, 'by', actorUsername, '-', description);
  } catch (error) {
    console.error('❌ Error logging activity:', error);
    // Don't throw error to prevent disrupting main functionality
  }
}

/**
 * Helper functions for specific activities
 */

export async function logPaymentAdded(
  roomNumber: string,
  residentName: string,
  amount?: number,
  category?: string
) {
  await logActivity({
    activityType: 'payment_added',
    description: `Kamar ${roomNumber} menambahkan bukti pembayaran`,
    roomNumber,
    residentName,
    metadata: { amount, category }
  });
}

export async function logPaymentDeleted(
  roomNumber: string,
  residentName: string
) {
  await logActivity({
    activityType: 'payment_deleted',
    description: `Kamar ${roomNumber} menghapus bukti pembayaran`,
    roomNumber,
    residentName
  });
}

export async function logResidentAdded(
  roomNumber: string,
  residentName: string
) {
  await logActivity({
    activityType: 'resident_added',
    description: `Kamar ${roomNumber} menambahkan penghuni baru: ${residentName}`,
    roomNumber,
    residentName
  });
}

export async function logResidentEdited(
  roomNumber: string,
  residentName: string,
  changes?: string[]
) {
  const changesText = changes && changes.length > 0 
    ? ` (${changes.join(', ')})` 
    : '';
  await logActivity({
    activityType: 'resident_edited',
    description: `Kamar ${roomNumber} memperbarui data penghuni${changesText}`,
    roomNumber,
    residentName,
    metadata: { changes }
  });
}

export async function logResidentMoved(
  fromRoom: string,
  toRoom: string,
  residentName: string
) {
  await logActivity({
    activityType: 'resident_moved',
    description: `Kamar ${fromRoom} memindahkan penghuni ke Kamar ${toRoom}`,
    roomNumber: toRoom,
    residentName,
    metadata: { fromRoom, toRoom }
  });
}

export async function logResidentDeleted(
  roomNumber: string,
  residentName: string
) {
  await logActivity({
    activityType: 'resident_deleted',
    description: `Kamar ${roomNumber} menghapus penghuni: ${residentName}`,
    roomNumber,
    residentName
  });
}

export async function logPaymentVerified(
  roomNumber: string,
  residentName: string,
  periodNumber: number
) {
  await logActivity({
    activityType: 'payment_verified',
    description: `Kamar ${roomNumber} memverifikasi pembayaran periode ${periodNumber}`,
    roomNumber,
    residentName,
    metadata: { periodNumber }
  });
}

export async function logRoomUpdated(
  roomNumber: string,
  changes: string
) {
  await logActivity({
    activityType: 'room_updated',
    description: `Kamar ${roomNumber} diperbarui: ${changes}`,
    roomNumber
  });
}
